import { ComboboxAndPopover } from '@/components/ui/combobox/combobox-and-popover';
import type { Option } from '@/components/ui/combobox/combobox-popover';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';
import type { RapidReconStep } from '@/models/types';
import { useRef, useState } from 'react';
import { RapidReconStepBadge } from './rapid-recon-step-badge';
import { RapidReconStepCombobox } from './rapid-recon-step-combobox';

export const getRapidReconStepOptions = (): Option<RapidReconStep>[] => {
  return [
    {
      value: 'None',
      label: 'None',
    },
    {
      value: 'Purchase/Auction',
      label: 'Purchase/Auction',
    },
    {
      value: 'Listing + DB Input',
      label: 'Listing + DB Input',
    },
    {
      value: 'In Transit',
      label: 'In Transit',
    },
    {
      value: 'Vehicle Hold',
      label: 'Vehicle Hold',
    },
    {
      value: 'Offsite Repairs',
      label: 'Offsite Repairs',
    },
    {
      value: 'Intake Inspection',
      label: 'Intake Inspection',
    },
    {
      value: 'One Shot Photo',
      label: 'One Shot Photo',
    },
    {
      value: 'Battery Diagnostics',
      label: 'Battery Diagnostics',
    },
    {
      value: 'Full Inspection',
      label: 'Full Inspection',
    },
    {
      value: 'Order Parts',
      label: 'Order Parts',
    },
    {
      value: 'Parts Arrived',
      label: 'Parts Arrived',
    },
    {
      value: 'Mechanical Repair',
      label: 'Mechanical Repair',
    },
    {
      value: 'Ready For Pickup',
      label: 'Ready For Pickup',
    },
    {
      value: 'Body Shop',
      label: 'Body Shop',
    },
    {
      value: 'Cosmetic',
      label: 'Cosmetic',
    },
    {
      value: 'Wheels',
      label: 'Wheels',
    },
    {
      value: 'Paintless Dent Repair',
      label: 'Paintless Dent Repair',
    },
    {
      value: 'Detail',
      label: 'Detail',
    },
    {
      value: 'Full Photos',
      label: 'Full Photos',
    },
    {
      value: 'Ready For Sale',
      label: 'Ready For Sale',
    },
    {
      value: 'Wholesale',
      label: 'Wholesale',
    },
    {
      value: 'Needs Attention',
      label: 'Needs Attention',
    },
  ];
};

export const RAPID_RECON_STEP_OPTIONS_MAP = getRapidReconStepOptions();

export type RapidReconStepSelectorProps = {
  id: Deal['id'];
  shortId: Deal['short_id'];
  value: RapidReconStep;
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  badgeClassName?: string;
};

export function RapidReconStepSelector({
  id,
  shortId,
  value,
  hideCopy = false,
  side = 'right',
  // DEV UX NOTE: do not turn `withHotkeyListener` in any list or board views
  // because it will lag the page. This is purely meant for limited number of
  // DealStatusSelectors on a page.
  withHotkeyListener = false,
  badgeClassName,
}: RapidReconStepSelectorProps) {
  const [open, setOpen] = useState(false);

  const statusButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div>
      <ComboboxAndPopover
        combobox={
          <RapidReconStepCombobox
            id={id}
            shortId={shortId}
            value={value}
            afterOnChange={() => {
              setOpen(false);
            }}
          />
        }
        side={side}
        align="start"
        open={open}
        setOpen={setOpen}
      >
        <div
        // DEV NOTE: without this div, combobox-popover won't work
        // that is, clicks will not open the combobox
        // I think the reason is children node needs to be able to receive a ref
        // in order to detect a click event, which makes it open
        // thus so this div is a workaround
        >
          <RapidReconStepBadge
            value={value}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            className={badgeClassName}
          />
        </div>
      </ComboboxAndPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.DEAL_RAPID_RECON_STEP}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
